import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { IReportEmbedConfiguration, models } from 'powerbi-client';
import { PbiEmbeddedService } from '../../services/pbi-embedded.service';
import { ChangePage, InitializePBI, SetPages, SetSelectedField, SetTitleFromSelectedField } from './pbiembedded.action';

// State model
export type PBIStateModel = {
  embedConfig: IReportEmbedConfiguration | null;
  accessToken: string | null;
  isInitialized: boolean;
  currentPage: string | null;
  initialPage?: string;
  selectedField?: string[];
  selectedTitle?: string[];
  pages: models.IPage[];
};

// Initial state
const defaults: PBIStateModel = {
  embedConfig: null,
  accessToken: null,
  isInitialized: false,
  currentPage: null,
  initialPage: null,
  selectedField: null,
  pages: []
};

@State<PBIStateModel>({
  name: 'pbi',
  defaults: {
    currentPage: '',
    embedConfig: undefined,
    accessToken: '',
    isInitialized: false,
    pages: []
  }
})
@Injectable()
export class PBIState {
  constructor(private readonly pbiService: PbiEmbeddedService) {}

  @Selector()
  static getSelectedField(state: PBIStateModel) {
    if (state.selectedField && state.selectedField.length > 0) {
      return state.selectedField;
    }
    const stored = localStorage.getItem('selectedField');
    return stored ? JSON.parse(stored) : undefined;
  }

  @Selector()
  static getTitleFromSelectedField(state: PBIStateModel) {
    if (state.selectedTitle && state.selectedTitle.length > 0) {
      return state.selectedTitle;
    }
    const stored = localStorage.getItem('selectedTitle');
    return stored ? JSON.parse(stored) : undefined;
  }

  @Selector()
  static getEmbedConfig(state: PBIStateModel) {
    return state.embedConfig;
  }

  @Selector()
  static getPages(state: PBIStateModel): models.IPage[] {
    return state.pages;
  }

  @Selector()
  static getCurrentPage(state: PBIStateModel) {
    return state.currentPage;
  }

  @Action(ChangePage)
  changePage({ patchState }: StateContext<PBIStateModel>, action: ChangePage) {
    patchState({
      currentPage: action.page
    });
  }

  @Action(SetPages)
  setPages({ patchState }: StateContext<PBIStateModel>, action: SetPages) {
    patchState({
      pages: action.pages
    });
  }

  @Action(SetSelectedField)
  setSelectedField({ patchState }: StateContext<PBIStateModel>, action: SetSelectedField) {
    localStorage.setItem('selectedField', JSON.stringify(action.field));
    patchState({
      selectedField: action.field
    });
  }

  @Action(SetTitleFromSelectedField)
  setTitleFromSelectedField({ patchState }: StateContext<PBIStateModel>, action: SetTitleFromSelectedField) {
    localStorage.setItem('selectedTitle', JSON.stringify(action.field));
    patchState({
      selectedTitle: action.field
    });
  }



  @Action(InitializePBI)
  initialize({ getState, patchState }: StateContext<PBIStateModel>, action: InitializePBI) {
    const state = getState();

    if (state.isInitialized) {
      if (state.currentPage !== action.initialPage) {
        patchState({
          currentPage: action.initialPage
        });
      }

      return;
    }

    return this.pbiService.getToken(action.input).pipe(
      tap((accessToken) => {
        const embedConfig = {
          type: 'report',
          id: action.input.reports[0].id,
          embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${action.input.reports[0].id}`,
          tokenType: models.TokenType.Embed,
          accessToken,
          settings: {
            filterPaneEnabled: false,
            navContentPaneEnabled: false,
            panes: {
              filters: {
                expanded: false,
                visible: false
              }
            }
          },
          pageName: action.initialPage,
        };
        patchState({
          embedConfig,
          accessToken,
          isInitialized: true,
          initialPage: action.initialPage
        });
      })
    );
  }
}
